import { queryOptions, useQuery } from '@tanstack/react-query'
import { CalendarPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api } from '@/api/client'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { Select } from '@/components/ui/Select'
import { studentsQuery } from '@/features/students/api'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { leaveTypeOptionsQuery, personBalancesQuery, useFileLeaveOnBehalf } from '../api'
import { formatDays, formatRange, type LeaveKind } from '../types'
import { LeaveRequestForm } from './LeaveRequestForm'
import { sectionLabel } from '@/features/academic/sectionLabel'

const staffOptionsQuery = () =>
  queryOptions({
    queryKey: ['staff', 'options'],
    queryFn: () => api.get<{ id: number; name: string; employeeCode: string }[]>('/staff/options'),
  })

/** Admins file leave on behalf of any staff member or student. */
export function FileLeaveDialog({ onOpenChange }: { onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('leave')
  const [kind, setKind] = useState<LeaveKind>('staff')
  const [personId, setPersonId] = useState('')
  const [personError, setPersonError] = useState<string | undefined>()
  const [search, setSearch] = useState('')
  const debounced = useDebouncedValue(search, 300)
  const [submitting, setSubmitting] = useState(false)
  const file = useFileLeaveOnBehalf()

  const staff = useQuery({ ...staffOptionsQuery(), enabled: kind === 'staff' })
  const students = useQuery({
    ...studentsQuery({ search: debounced || undefined, perPage: 20, status: 'active' }),
    enabled: kind === 'student',
  })
  const types = useQuery(leaveTypeOptionsQuery(kind))
  const balances = useQuery({ ...personBalancesQuery(kind, Number(personId)), enabled: personId !== '' })

  const personOptions =
    kind === 'staff'
      ? (staff.data ?? []).map((s) => ({ value: s.id, label: `${s.name} (${s.employeeCode})` }))
      : (students.data?.data ?? []).map((s) => ({
          value: s.id,
          label: `${s.fullName} (${s.studentCode})${s.currentEnrollment ? ` · ${sectionLabel(s.currentEnrollment.className, s.currentEnrollment.sectionName)}` : ''}`,
        }))

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="lg"
      dismissible={!submitting}
      icon={<CalendarPlus />}
      title={t('File leave on behalf')}
      description={t('Record leave for a staff member or a student. It starts as pending until approved.')}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="file-leave-form" loading={submitting} disabled={!types.data}>
            {t('File request')}
          </Button>
        </>
      }
    >
      <LeaveRequestForm
        key={kind}
        formId="file-leave-form"
        types={types.data ?? []}
        balances={balances.data}
        onSubmittingChange={setSubmitting}
        onSubmit={async (values) => {
          if (!personId) {
            setPersonError(kind === 'staff' ? t('Choose a staff member.') : t('Choose a student.'))
            return
          }
          const id = Number(personId)
          const created = await file.mutateAsync({
            ...values,
            kind,
            staffId: kind === 'staff' ? id : null,
            studentId: kind === 'student' ? id : null,
          })
          toast.success(
            t('Leave filed'),
            `${created.person.name} : ${formatRange(created)} (${formatDays(created.days)}).`,
          )
          onOpenChange(false)
        }}
      >
        <fieldset className="space-y-3">
          <legend className="mb-1.5 text-[0.8125rem] font-medium text-ink-soft">{t('Leave for')}</legend>
          <RadioGroup
            aria-label={t('Leave for')}
            orientation="horizontal"
            value={kind}
            onValueChange={(v) => {
              setKind(v as LeaveKind)
              setPersonId('')
              setPersonError(undefined)
            }}
            options={[
              { value: 'staff', label: t('Staff member') },
              { value: 'student', label: t('Student') },
            ]}
          />
        </fieldset>
        {kind === 'student' ? (
          <FormField label={t('Find a student')} hint={t('Search by name, code or admission number.')}>
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('Search students')}
            />
          </FormField>
        ) : null}
        <FormField label={kind === 'staff' ? t('Staff member') : t('Student')} required error={personError}>
          <Select
            placeholder={
              kind === 'staff'
                ? t('Choose a staff member')
                : students.isFetching
                  ? t('Searching…')
                  : t('Choose a student')
            }
            options={personOptions}
            value={personId}
            onChange={(e) => {
              setPersonId(e.target.value)
              setPersonError(undefined)
            }}
          />
        </FormField>
      </LeaveRequestForm>
    </Dialog>
  )
}
